import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_DOMAIN_AUTH } from '@scio3/graphql-client';
import { resolveBranding, type BrandingData } from '@scio3/shared-types';
import { isWhiteLabelBuild } from '@/lib/app-variant';
import { useBrandingTheme } from './useBrandingTheme';

/** Render-safe fields returned by the @Public `getDomainAuth` query (subset of BrandingData). */
interface PublicBranding {
  _id?: string;
  accountId?: string;
  host?: string;
  subHost?: string;
  siteTitle?: string;
  favicon?: string;
  logo?: string;
  logoCompany?: string;
  backgoundLogin?: string;
  loginBox?: string;
  loginBoxColor?: string;
  loginBoxPadding?: number;
  loginBoxOpacity?: number;
  loginBoxMarginLeftRight?: number;
  loginBoxMarginTopBottom?: number;
  colorTheme?: string;
  removeOptiSignsBranding?: boolean;
  hideCreateAccountButton?: boolean;
  hideOptiSignsBrandedApps?: boolean;
  isAllowGoogleLoginSocial?: boolean;
  isAllowFBLoginSocial?: boolean;
  isAllowMSLoginSocial?: boolean;
}
interface GetDomainAuthData {
  getDomainAuth: PublicBranding | null;
}
interface GetDomainAuthVars {
  host: string;
  accountId?: string;
}

/**
 * scio1 parity: the pre-login auth URLs carry the account whose branding to show —
 * `/signIn/:accountId` (also signUp / forgotPassword / passwordReset). Resolving by accountId is
 * required because many accounts can share one subHost as child hosts (isChildHost:true) with
 * DIFFERENT configs, so a host-only lookup returns the wrong (parent) branding. Excludes the static
 * signIn sub-routes (optidev / mfa / saml), which are not account ids.
 */
function accountIdFromAuthPath(pathname: string): string | undefined {
  const seg = pathname.match(/\/(?:signIn|signUp|forgotPassword|passwordReset)\/([^/?#]+)/i)?.[1];
  if (!seg || ['optidev', 'mfa', 'saml'].includes(seg)) return undefined;
  return seg;
}

interface BrandingContextValue {
  /** Resolved white-label branding for this host; null on the normal build or when no domain matches. */
  branding: BrandingData | null;
  loading: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({ branding: null, loading: false });

/** Consume the host-resolved white-label branding (null on non-white-label builds / hosts). */
export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

const AWS_BUCKET = (import.meta.env.VITE_AWS_BUCKET_NAME as string | undefined) ?? '';

/**
 * Host-based branding bootstrap for the white-label build. When `VITE_IS_WHITE_LABEL` is set, fetches
 * the PUBLIC `getDomainAuth(host)` (pre-login, no token), resolves S3 URLs, applies the portal theme
 * (color/favicon/title), and exposes the branding to the auth shell via `useBranding()`.
 *
 * On the normal `app.optisigns.com` build the query is skipped → branding is null → default OptiSigns
 * look. A fetch error is swallowed (errorPolicy 'all' → no data → default), so a branding failure can
 * never block login. Must be mounted inside ApolloProvider.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const enabled = isWhiteLabelBuild();
  const host = typeof window !== 'undefined' ? window.location.host : '';
  // scio1 parity: prefer the accountId carried in the /signIn/:accountId (etc.) URL over the host,
  // so a shared subHost resolves to the RIGHT account's branding (not the parent's).
  const accountId =
    typeof window !== 'undefined' ? accountIdFromAuthPath(window.location.pathname) : undefined;

  const { data, loading } = useQuery<GetDomainAuthData, GetDomainAuthVars>(GET_DOMAIN_AUTH, {
    variables: { host, accountId },
    skip: !enabled || (!host && !accountId),
    // cache-and-network (NOT cache-first): the Apollo cache is persisted to IndexedDB
    // (useCachePersistence), so cache-first would pin whatever getDomainAuth returned on the
    // first-ever visit — including a `null` from before the host's domain record existed —
    // and never reflect later DB edits. Revalidate every mount: paint from cache instantly,
    // then update from the network so branding always tracks the current record.
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // swallow + render: a branding failure must not block login
  });

  const branding = useMemo<BrandingData | null>(() => {
    const raw = data?.getDomainAuth;
    return raw ? resolveBranding({ ...raw, isWhiteLabel: true }, AWS_BUCKET) : null;
  }, [data]);

  useBrandingTheme(branding);

  const value = useMemo<BrandingContextValue>(() => ({ branding, loading }), [branding, loading]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
